import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import AuthLayout from "../components/AuthLayout";
import api, { errorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, Mail, MailCheck, Info, ArrowLeft, ArrowRight } from "lucide-react";

export default function ForgotPassword() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (user) navigate("/pipeline");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      kicker="Des pharmacies plus fortes, pour des communautés en meilleure santé."
      headlineLines={["Simplifiez la gestion", "de vos opportunités."]}
      quote="Construire aujourd'hui le réseau de pharmacies de demain."
    >
      <div className="flex flex-col items-center text-center mb-8">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-jamora-50 mb-5">
          {submitted ? <MailCheck size={26} className="text-jamora" /> : <Mail size={26} className="text-jamora" />}
        </div>
        <h2 className="font-serif-display text-3xl font-bold text-slate-900">Mot de passe oublié ?</h2>
        <p className="text-sm text-slate-500 mt-2 max-w-[340px]">
          {submitted
            ? "Vérifiez votre boîte mail pour continuer la réinitialisation."
            : "Saisissez l'adresse e-mail associée à votre compte. Nous vous enverrons un lien de réinitialisation de votre mot de passe."}
        </p>
      </div>

      {!submitted && (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Adresse e-mail</Label>
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                id="email"
                data-testid="input-forgot-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                className="pl-10 h-11"
              />
            </div>
          </div>
          <Button
            type="submit"
            data-testid="btn-forgot-submit"
            disabled={loading}
            className="w-full h-11"
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : "Envoyer le lien"}
            {!loading && <ArrowRight size={16} />}
          </Button>
        </form>
      )}

      <div className="flex items-center gap-3 my-6">
        <div className="h-px flex-1 bg-slate-200" />
        <span className="text-xs text-slate-400">Retour</span>
        <div className="h-px flex-1 bg-slate-200" />
      </div>

      <div className="text-center">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-jamora hover:underline"
        >
          <ArrowLeft size={14} />
          Retour à la connexion
        </Link>
      </div>

      <div className="mt-6 flex items-start gap-3 rounded-lg border border-jamora/15 bg-jamora-50 p-4">
        <Info size={16} className="mt-0.5 flex-shrink-0 text-jamora" />
        <p className="text-xs text-slate-600 leading-relaxed">
          Si un compte est associé à cette adresse, un lien de réinitialisation vous sera envoyé.
          Pour des raisons de sécurité, nous ne confirmons pas si un compte existe ou non.
        </p>
      </div>
    </AuthLayout>
  );
}
